import { Box, Chip } from "@mui/material";
import ReactECharts from "echarts-for-react";
import { useEffect, useMemo, useState } from "react";
import { mesfColors } from "@/setup/ag-grid/mesf-theme";
import { useStraightenerParameters } from "../../straightener-common/hooks/useStraightenerParameters";
import type { StraightenerTrendRow } from "../models/StraightenerTrend";

export const COLOR_TARGET = "#2A32FF";
// The theme's success green (the Set as Current button): the previous dark olive
// read almost black against the blue Target line.
const COLOR_ACTUAL = "#2e7d32";
const COLOR_BAND = "#E60000";

const SERIES_COLORS = {
	Target: COLOR_TARGET,
	Actual: COLOR_ACTUAL,
	Max: COLOR_BAND,
	Min: COLOR_BAND,
} as const;

type SeriesName = keyof typeof SERIES_COLORS;
const SERIES_NAMES: SeriesName[] = ["Target", "Actual", "Max", "Min"];

export const StraightenerTrendChart = ({
	rows,
}: {
	rows: StraightenerTrendRow[];
}) => {
	const { parameters, rollCount } = useStraightenerParameters();
	// Only the monitored per-roll metrics (Vert Pos, Axial Pos) — min/max bands.
	const perRollParams = useMemo(
		() => parameters.filter((p) => p.hasMinMax),
		[parameters],
	);
	const [paramCode, setParamCode] = useState("");
	useEffect(() => {
		if (!paramCode && perRollParams.length) {
			setParamCode(perRollParams[0].code);
		}
	}, [paramCode, perRollParams]);
	const [visible, setVisible] = useState<Record<SeriesName, boolean>>({
		Target: true,
		Actual: true,
		Max: true,
		Min: true,
	});
	const toggle = (n: SeriesName) =>
		setVisible((prev) => ({ ...prev, [n]: !prev[n] }));

	const option = useMemo(() => {
		const rolls = Array.from({ length: rollCount }, (_, i) => i + 1);
		const pick = (k: keyof StraightenerTrendRow) =>
			rolls.map((roll) => {
				const r = rows.find(
					(x) => x.parameterCode === paramCode && x.rollNumber === roll,
				);
				return r ? (r[k] as number | null) : null;
			});
		return {
			animation: true,
			animationDuration: 0,
			animationDurationUpdate: 400,
			animationEasingUpdate: "cubicOut",
			tooltip: {
				trigger: "axis",
				valueFormatter: (v: number | null) =>
					v == null ? "—" : Number(v).toFixed(3),
			},
			legend: { show: false, selected: visible },
			grid: { left: 50, right: 24, top: 12, bottom: 32 },
			xAxis: {
				type: "category",
				data: rolls.map((r) => `Roll #${r}`),
				axisLabel: { fontSize: 11 },
			},
			yAxis: { type: "value" },
			series: [
				{
					name: "Target",
					type: "line",
					data: pick("recipe"),
					itemStyle: { color: COLOR_TARGET },
					lineStyle: { color: COLOR_TARGET, width: 2 },
					symbolSize: 6,
					connectNulls: true,
				},
				{
					name: "Actual",
					type: "line",
					data: pick("current"),
					itemStyle: { color: COLOR_ACTUAL },
					lineStyle: { color: COLOR_ACTUAL, width: 2 },
					symbolSize: 7,
					connectNulls: true,
				},
				{
					name: "Max",
					type: "line",
					data: pick("max"),
					itemStyle: { color: COLOR_BAND },
					lineStyle: { color: COLOR_BAND, width: 1 },
					symbolSize: 5,
					connectNulls: true,
				},
				{
					name: "Min",
					type: "line",
					data: pick("min"),
					itemStyle: { color: COLOR_BAND },
					lineStyle: { color: COLOR_BAND, width: 1 },
					symbolSize: 5,
					connectNulls: true,
				},
			],
		};
	}, [rows, paramCode, visible]);

	return (
		<Box>
			<Box
				sx={{
					display: "flex",
					gap: 0.75,
					mb: 0.75,
					alignItems: "center",
					justifyContent: "space-between",
					flexWrap: "wrap",
				}}
			>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 0.5,
						p: 0.5,
						border: `1px solid ${mesfColors.grey[200]}`,
						borderRadius: 1.5,
						flexWrap: "wrap",
					}}
				>
					{perRollParams.map((p) => {
						const active = paramCode === p.code;
						return (
							<Chip
								key={p.code}
								size="small"
								label={p.displayName}
								onClick={() => setParamCode(p.code)}
								sx={{
									cursor: "pointer",
									fontWeight: 600,
									fontSize: "0.72rem",
									height: 24,
									borderRadius: 1.5,
									bgcolor: active ? "primary.main" : "transparent",
									color: active ? "#fff" : mesfColors.text.secondary,
									border: active ? "none" : `1px solid ${mesfColors.grey[200]}`,
									"&:hover": {
										bgcolor: active ? "primary.dark" : mesfColors.grey[50],
									},
								}}
							/>
						);
					})}
				</Box>

				<Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
					{SERIES_NAMES.map((name) => {
						const active = visible[name];
						const color = SERIES_COLORS[name];
						return (
							<Chip
								key={name}
								size="small"
								label={name}
								onClick={() => toggle(name)}
								icon={
									<Box
										component="span"
										sx={{
											width: 9,
											height: 9,
											borderRadius: "50%",
											bgcolor: active ? color : mesfColors.grey[300],
											ml: "8px !important",
											flexShrink: 0,
										}}
									/>
								}
								sx={{
									cursor: "pointer",
									fontWeight: 600,
									fontSize: "0.72rem",
									height: 22,
									borderRadius: 1.5,
									border: `1px solid ${mesfColors.grey[200]}`,
									bgcolor: active
										? mesfColors.background.paper
										: mesfColors.grey[50],
									color: active
										? mesfColors.text.primary
										: mesfColors.text.secondary,
								}}
							/>
						);
					})}
				</Box>
			</Box>

			<ReactECharts
				option={option}
				notMerge={false}
				lazyUpdate
				style={{ height: 205, width: "100%" }}
			/>
		</Box>
	);
};
